/**
 * Legendary arts (วิชาในตำนาน of the weekly tournament, C0-TOURNEY; docs/design/ideas/2026-10-11-weekly-tournament.md).
 * An art marked `legendary` in its data (data/dragonPalm.ts) is a prize: the champion of ประลองยุทธภพ holds it for one
 * week and must defend it. Rules, all here so the World reads one place:
 * - full power (`vsOthers`, about 1.6 times the best ordinary art) against NPCs, monsters and bosses;
 * - normalised against players, anywhere (`vsPlayer`: set so a holder and a user of the best ordinary art are even,
 *   measured by the bot-vs-bot simulation in test/legendaryBalance.test.ts), and a damage buff (the stomp's war cry)
 *   adds at most `playerBuffCap` to a legendary blow on a player;
 * - the ground-targeted dragon from the sky winds up at least `warn` seconds (the cast shows where and sounds the
 *   power-move warning), always;
 * - never tradeable.
 * Data and pure functions only.
 */
import { MARTIAL_ARTS, artOfSkill, type MartialArtDef } from './skills';

export const LEGENDARY = {
  /** The best ordinary art's power as 1: a legendary art's blows on NPCs, monsters and bosses. */
  vsOthers: 1.6,
  /** A legendary art's blows on a player (a duel, a PK, a tournament bout): tuned by the balance simulation. */
  vsPlayer: 0.82,
  /** The most a damage-dealt buff (the stomp's war cry, ×1.25) may add to a legendary blow on a player. */
  playerBuffCap: 1.1,
  /** Seconds of ground warning before the dragon from the sky lands (never shorter). */
  warn: 0.6,
} as const;

/** The art's legendary definition, or undefined for an ordinary art. */
export const legendaryArt = (artId: string): MartialArtDef | undefined => {
  const a = MARTIAL_ARTS[artId];
  return a?.legendary ? a : undefined;
};

/** All legendary arts. */
export const legendaryArts = (): MartialArtDef[] => Object.values(MARTIAL_ARTS).filter((a) => a.legendary);

/** Whether a move belongs to a legendary art. */
export const isLegendarySkill = (skillId: string): boolean => !!artOfSkill(skillId)?.legendary;

/** A legendary art cannot be traded, gifted or sold (any trade code asks here). */
export const artTradeable = (artId: string): boolean => !MARTIAL_ARTS[artId]?.legendary;

/** The power multiplier of a blow from `art` on a target: 1 for ordinary arts. */
export function legendaryPower(art: MartialArtDef | undefined, targetIsPlayer: boolean): number {
  if (!art?.legendary) return 1;
  return targetIsPlayer ? LEGENDARY.vsPlayer : LEGENDARY.vsPlayer * LEGENDARY.vsOthers;
}

/** A damage-dealt multiplier as it applies to a blow of `art` on a target (capped when it is a legendary blow on a player). */
export function legendaryDealt(art: MartialArtDef | undefined, targetIsPlayer: boolean, dealt: number): number {
  return art?.legendary && targetIsPlayer ? Math.min(dealt, LEGENDARY.playerBuffCap) : dealt;
}

/** Who holds a legendary art, and how long. */
export interface HolderRecord {
  readonly art: string;
  /** A player (the one at this screen) or a wanderer (by id), and the name shown. */
  readonly who: 'player' | 'wanderer' | 'other';
  readonly id: string;
  readonly name: string;
  /** The tournament week it was won (counted from the first). */
  readonly week: number;
  /** Weeks won in a row, this one included. */
  readonly streak: number;
}
